import { useState } from 'react';
import { Link } from 'react-router-dom';

export interface FAQItem { q: string; a: string; }
export interface FAQContent { badge?: string; heading: string; items: FAQItem[]; }
interface Props { content: FAQContent; }

export default function FAQSection({ content }: Props) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="w-full bg-[#f5f0e8] py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-10 bg-gold"></div>
          <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-gold">{content.badge ?? 'FAQ'}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Accordion */}
          <div className="lg:col-span-7">
            <h2 className="font-serif text-forest font-semibold leading-tight mb-12" style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)' }}>
              {content.heading}
            </h2>
            <div className="space-y-2">
              {content.items.map((item, i) => {
                const isOpen = open === i;
                return (
                  <div key={i} className={`rounded-xl overflow-hidden border transition-all duration-200 ${isOpen ? 'border-gold/35 bg-white shadow-md shadow-gold/8' : 'border-stone/50 bg-white/60 hover:border-stone'}`}>
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer group"
                      aria-expanded={isOpen}
                    >
                      <div className="flex items-center gap-4">
                        <span className="font-serif text-sm text-gold/50 w-7 shrink-0 select-none" aria-hidden="true">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className={`font-sans font-semibold text-sm leading-snug transition-colors ${isOpen ? 'text-forest' : 'text-charcoal-mid'}`}>
                          {item.q}
                        </span>
                      </div>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 transition-all duration-200 ${isOpen ? 'bg-gold text-forest-deep rotate-45' : 'bg-stone/50 text-charcoal-muted group-hover:bg-stone'}`}>
                        <i className="ri-add-line text-xs"></i>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-6 pl-[4.25rem]">
                        <p className="font-sans text-sm text-charcoal-muted leading-relaxed">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sticky contact card */}
          <div className="lg:col-span-5">
            <div className="sticky top-28 bg-[#07140a] rounded-2xl p-9 text-white">
              <div className="h-px w-8 bg-gold mb-6"></div>
              <h3 className="font-serif text-xl font-semibold mb-3">Have a specific question?</h3>
              <p className="font-sans text-white/45 text-sm leading-relaxed mb-8">
                Our team is available 6 days a week. Call us directly or submit an estimate request and we'll get back to you same day.
              </p>
              <div className="space-y-4 mb-8">
                <a href="tel:+12086596067" className="flex items-center gap-3 group cursor-pointer">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold/15 group-hover:bg-gold transition-all shrink-0">
                    <i className="ri-phone-fill text-gold group-hover:text-forest-deep text-sm transition-colors"></i>
                  </div>
                  <div>
                    <div className="font-sans font-bold text-white text-base group-hover:text-gold transition-colors">(208) 659-6067</div>
                    <div className="font-sans text-white/30 text-xs">Call or text anytime</div>
                  </div>
                </a>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/8 shrink-0">
                    <i className="ri-time-line text-white/50 text-sm"></i>
                  </div>
                  <div className="font-sans text-white/50 text-xs">
                    Mon–Sat: 7:30AM–6:00PM<br />
                    Sun: 8:00AM–5:00PM<br />
                    <span className="text-gold">24/7 Emergency Service</span>
                  </div>
                </div>
              </div>
              <Link to="/#estimate" className="w-full whitespace-nowrap inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-forest-deep font-bold text-sm rounded-xl py-3.5 transition-all cursor-pointer shadow-xl shadow-gold/25">
                Get Free Estimate <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
